import { satTempFromPressure } from '../data/pt-charts.js'

export type PressureUnit = 'psig' | 'psi' | 'bar' | 'kpa'
export type TempUnit = 'f' | 'c'
export type MeteringDevice = 'txv' | 'fixed'
export type StatusLevel = 'low' | 'normal' | 'high'

export interface SuperheatSubcoolingInput {
    refrigerantId:string;
    suctionPressure:number;
    suctionLineTemp:number;
    liquidPressure:number;
    liquidLineTemp:number;
    pressureUnit:PressureUnit;
    tempUnit:TempUnit;
    meteringDevice:MeteringDevice;
}

export interface Reading {
    value:number;
    status:StatusLevel;
    recommendation:string;
}

export interface SuperheatSubcoolingResult {
    evapSatTemp:number;
    condSatTemp:number;
    superheat:Reading;
    subcooling:Reading;
}

export function celsiusToFahrenheit (c:number):number {
    return c * 9 / 5 + 32
}

/** Convert any supported pressure unit to PSIG (gauge). */
export function toPsig (pressure:number, unit:PressureUnit):number {
    switch (unit) {
        case 'psig': return pressure
        case 'psi': return pressure - 14.696   // PSIA → PSIG
        case 'bar': return pressure * 14.5038  // barg → PSIG
        case 'kpa': return pressure * 0.145038 // kPag → PSIG
    }
}

const THRESHOLDS:Record<MeteringDevice, {
    superheat: { low:number; high:number };
    subcooling: { low:number; high:number };
}> = {
    txv: {
        superheat: { low: 8, high: 14 },
        subcooling: { low: 10, high: 20 },
    },
    fixed: {
        superheat: { low: 10, high: 18 },
        subcooling: { low: 5, high: 15 },
    },
}

function classify (value:number, low:number, high:number):StatusLevel {
    if (value < low) return 'low'
    if (value > high) return 'high'
    return 'normal'
}

const SUPERHEAT_REC:Record<MeteringDevice, Record<StatusLevel, string>> = {
    txv: {
        low: 'Superheat is low; risk of liquid slugging. System may be overcharged or TXV opening too wide.',
        normal: 'Superheat is within the normal range for a TXV/EEV system.',
        high: 'Superheat is high; system may be undercharged or the TXV may need adjustment.',
    },
    fixed: {
        low: 'Superheat is low; system may be overcharged. Recover refrigerant if needed.',
        normal: 'Superheat is within the normal range for a fixed-orifice system.',
        high: 'Superheat is high; system is likely undercharged. Add refrigerant.',
    },
}

const SUBCOOLING_REC:Record<MeteringDevice, Record<StatusLevel, string>> = {
    txv: {
        low: 'Subcooling is low; system may be undercharged.',
        normal: 'Subcooling is within the normal range.',
        high: 'Subcooling is high; system may be overcharged or the liquid line may be restricted.',
    },
    fixed: {
        low: 'Subcooling is low; check refrigerant charge.',
        normal: 'Subcooling is within the normal range.',
        high: 'Subcooling is high; system may be overcharged.',
    },
}

export function calcSuperheatSubcooling (
    input:SuperheatSubcoolingInput
):SuperheatSubcoolingResult | null {
    const {
        refrigerantId,
        suctionPressure,
        suctionLineTemp,
        liquidPressure,
        liquidLineTemp,
        pressureUnit,
        tempUnit,
        meteringDevice,
    } = input

    const suctionPsig = toPsig(suctionPressure, pressureUnit)
    const liquidPsig = toPsig(liquidPressure, pressureUnit)

    const suctionTempF = tempUnit === 'c' ?
        celsiusToFahrenheit(suctionLineTemp) :
        suctionLineTemp
    const liquidTempF = tempUnit === 'c' ?
        celsiusToFahrenheit(liquidLineTemp) :
        liquidLineTemp

    const evapSatTemp = satTempFromPressure(refrigerantId, suctionPsig)
    const condSatTemp = satTempFromPressure(refrigerantId, liquidPsig)

    if (evapSatTemp === null || condSatTemp === null) return null

    const superheatVal = suctionTempF - evapSatTemp
    const subcoolingVal = condSatTemp - liquidTempF

    const thresholds = THRESHOLDS[meteringDevice]

    const superheatStatus = classify(
        superheatVal,
        thresholds.superheat.low,
        thresholds.superheat.high
    )
    const subcoolingStatus = classify(
        subcoolingVal,
        thresholds.subcooling.low,
        thresholds.subcooling.high
    )

    return {
        evapSatTemp,
        condSatTemp,
        superheat: {
            value: superheatVal,
            status: superheatStatus,
            recommendation: SUPERHEAT_REC[meteringDevice][superheatStatus],
        },
        subcooling: {
            value: subcoolingVal,
            status: subcoolingStatus,
            recommendation: SUBCOOLING_REC[meteringDevice][subcoolingStatus],
        },
    }
}
